<template>
  <v-card flat>
    <v-card-text>
      <v-alert v-if="error" type="error" dismissible @input="error = null">
        <span v-text="error" />
      </v-alert>

      <v-alert v-if="successMessage" type="success" dismissible @input="successMessage = null">
        <span v-text="successMessage" />
      </v-alert>

      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedConfig"
              :items="sdkConfigurations"
              :loading="loadingConfigurations"
              :disabled="submitting"
              item-text="name"
              item-value="id"
              label="Select SDK Configuration"
              hint="Choose an SDK configuration to force update"
              persistent-hint
              return-object
              clearable
              @change="onConfigSelect"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Value: {{ item.value }} | Status: {{ item.status }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.value"
              :rules="valueRules"
              :disabled="!selectedConfig || submitting"
              label="Value"
              hint="The SDK configuration value"
              counter="255"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              v-model="formData.status"
              :items="statuses"
              :rules="statusRules"
              :disabled="!selectedConfig || submitting"
              label="Status"
              required
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="formData.description"
              :disabled="!selectedConfig || submitting"
              label="Description"
              hint="Optional description"
              counter="255"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-btn
              color="primary"
              :disabled="!isFormValid"
              :loading="submitting"
              @click="submitForceUpdate"
            >
              Force Update
            </v-btn>
            <v-btn text :disabled="submitting" class="ml-2" @click="resetForm">
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import HTTP from '@/api/dog';
import { enumFetch } from '@nielsen-media/ui-toolkit-vue-api';
import vrb from 'bsm-validation-rules';

export default {
  name: 'ForceUpdateSDKConfigForm',
  props: {
    templateId: {
      type: Number,
      required: true,
      validator: value => value > 0,
    },
  },
  data() {
    return {
      valid: false,
      sdkConfigurations: [],
      selectedConfig: null,
      originalValues: {},
      formData: {
        value: '',
        status: '',
        description: '',
      },
      loadingConfigurations: false,
      submitting: false,
      statuses: [],
      error: null,
      successMessage: null,
      valueRules: [vrb.presence(), vrb.typeOf('string'), vrb.length({ max: 255 })],
      statusRules: [vrb.presence(), vrb.typeOf('string')],
      abortController: null,
    };
  },
  computed: {
    isFormValid() {
      return this.valid && this.selectedConfig && this.hasChanges && !this.submitting;
    },
    hasChanges() {
      if (!this.selectedConfig) return false;

      return (
        this.formData.value !== this.originalValues.value ||
        this.formData.status !== this.originalValues.status ||
        this.formData.description !== this.originalValues.description
      );
    },
  },
  watch: {
    templateId: {
      immediate: true,
      handler() {
        this.fetchSDKConfigurations();
      },
    },
  },
  created() {
    this.enumFetch(HTTP, {
      statuses: '/sdk_configurations/enum/statuses',
    });
  },
  methods: {
    enumFetch,

    async fetchSDKConfigurations() {
      if (!this.templateId) return;

      // Cancel any pending request
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.loadingConfigurations = true;
      this.error = null;

      try {
        const response = await HTTP.get(
          `/applications/templates/${this.templateId}/sdk_configurations`,
          {
            signal: this.abortController.signal,
          },
        );
        this.sdkConfigurations = response.data;
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.error = err.response?.data?.message || 'Failed to load SDK configurations';
        }
      } finally {
        this.loadingConfigurations = false;
      }
    },

    onConfigSelect(config) {
      if (!config) {
        this.resetForm();
        return;
      }

      // Pre-fill form with current configuration values
      this.formData.value = config.value || '';
      this.formData.status = config.status || '';
      this.formData.description = config.description || '';

      // Store original values for change detection
      this.originalValues = {
        value: config.value || '',
        status: config.status || '',
        description: config.description || '',
      };

      // Clear any previous messages
      this.error = null;
      this.successMessage = null;
    },

    getChangedFields() {
      const changes = {};

      if (this.formData.value !== this.originalValues.value) {
        changes.value = this.formData.value;
      }
      if (this.formData.status !== this.originalValues.status) {
        changes.status = this.formData.status;
      }
      if (this.formData.description !== this.originalValues.description) {
        changes.description = this.formData.description;
      }

      return changes;
    },

    async submitForceUpdate() {
      if (!this.isFormValid) return;

      this.submitting = true;
      this.error = null;
      this.successMessage = null;

      try {
        const changedFields = this.getChangedFields();

        await HTTP.patch(
          `/applications/templates/sdk_configurations/${this.selectedConfig.id}/force_update`,
          changedFields,
        );

        this.successMessage = `SDK Configuration ${this.selectedConfig.name} force updated successfully!`;

        // Emit success event to parent
        this.$emit('success', {
          config: this.selectedConfig,
          changes: changedFields,
        });

        // Refresh SDK configurations list
        await this.fetchSDKConfigurations();

        // Reset form after successful update (but keep success message)
        const successMsg = this.successMessage;
        this.resetForm();
        this.successMessage = successMsg;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to force update SDK configuration';

        // Emit error event to parent
        this.$emit('error', {
          config: this.selectedConfig,
          error: this.error,
        });
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.selectedConfig = null;
      this.formData = {
        value: '',
        status: '',
        description: '',
      };
      this.originalValues = {};
      this.error = null;
      this.successMessage = null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped>
.v-card {
  padding: 16px;
}
</style>
